import React, { useState } from "react";
import { Dropdown, Tooltip } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Icons from "components/icons";
import { Localize } from "components/service";
import { ModalStandard, EntityRemoveButton } from "components/service";
import { userUseMutation } from "../hooks";
import { TUser } from "../helpers/user-types-helper";
import { usePermissionByObject } from "components/permissions";
import UserConst from "../users-const";
import { localizeByKeyStatic, localizeByKey } from "components/service/localize";
import UserAction from "../actions";

type TUserActionFieldProps = {
  userID: TUser[ "id" ];
  userName: TUser[ "name" ];
};

const UserActionField: React.FC<TUserActionFieldProps> = ({ userID, userName }) =>
{
  const navigate = useNavigate();
  const [ showSendPasswordModal, setShowSendPasswordModal ] = useState(false);

  const { _setMutationUserDelete, loadingMutationUserDelete } =
    userUseMutation.delete(userID);

  const { hasPermission: hasUpdatePermission } = usePermissionByObject(UserConst.permissions.UPDATE);
  const { hasPermission: hasDeletePermission } = usePermissionByObject(UserConst.permissions.DELETE);
  const { hasPermission: hasChangePasswordPermission } = usePermissionByObject(UserConst.permissions.CHANGE_PASSWORD);

  const items = [
    {
      key: "1",
      disabled: !hasUpdatePermission,
      onClick: hasUpdatePermission ? () => navigate(`${UserConst.basePath}/${userID}`) : undefined,
      label: (
        <Tooltip
          title={!hasUpdatePermission ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(UserConst.permissions.UPDATE.slug)}` : undefined}
        >
          <span style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Icons.Edit />
            <Localize>MENUS_DROP.Label_Edit</Localize>
          </span>
        </Tooltip>
      ),
    },
    {
      key: "2",
      disabled: !hasChangePasswordPermission,
      onClick: hasChangePasswordPermission ? () => setShowSendPasswordModal(true) : undefined,
      label: (
        <Tooltip
          title={!hasChangePasswordPermission ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(UserConst.permissions.CHANGE_PASSWORD.slug)}` : undefined}
        >
          <span style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Icons.SendPass />
            <Localize>MENUS_DROP.Label_SendPass</Localize>
          </span>
        </Tooltip>
      ),
    },
    {
      key: "3",
      disabled: !hasDeletePermission,
      label: (
        <EntityRemoveButton
          modalButton={
            <Tooltip
              title={!hasDeletePermission ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(UserConst.permissions.DELETE.slug)}` : undefined}
            >
              <span style={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Icons.Delete />
                <Localize>MENUS_DROP.Label_Delete</Localize>
              </span>
            </Tooltip>
          }
          nameEntity={localizeByKey("ENTITY.Modal_Title_User")}
          dataNameEntity={userName}
          loading={loadingMutationUserDelete}
          deleteMutation={(options) => {
            if (!hasDeletePermission) {
              return Promise.reject(new Error('Access denied'));
            }
            return _setMutationUserDelete(options);
          }}
          variables={{ id: userID }}
          disabled={!hasDeletePermission}
        />
      ),
    },
  ];

  return (
    <>
      <Dropdown
        placement="bottomRight"
        menu={{ items }}
        overlayClassName="drop-more-menu"
        arrow
      >
        <EllipsisOutlined className="btn-more" />
      </Dropdown>

      {showSendPasswordModal && (
        <ModalStandard
          width={400}
          showModal={showSendPasswordModal}
          setShowModal={setShowSendPasswordModal}
        >
          <UserAction.Fields.SendPass userID={userID} action={() => setShowSendPasswordModal(false)} />
        </ModalStandard>
      )}
    </>
  );
};

export default UserActionField;